import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  organizations as organizationsApi,
  tasks as tasksApi,
  type ContextTask,
} from "../api/resources";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Loading,
  PageHeader,
  Stat,
} from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { relativeTime } from "../lib/format";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";

/**
 * My Context Tasks — the employee's front door. What still needs their input, in plain words,
 * with one action each. An operator (tasks:assign) can widen the view to everyone's tasks and
 * assign or waive them. Nothing here is administration; nothing here talks to Enterprise Context.
 */
const TYPE_LABEL: Record<ContextTask["task_type"], string> = {
  ANSWER_QUESTION: "Question",
  CONFIRM_OBSERVATION: "Confirm",
  CLARIFY_CONFLICT: "Clarify",
  UPLOAD_EVIDENCE: "Upload",
  REVIEW_CHANGE: "Review",
  RECERTIFY_CONTEXT: "Recertify",
};

interface PersonOption {
  id: string;
  full_name: string;
}

const ORIGIN_LABEL: Record<string, string> = {
  ec_gap: "Enterprise Context needs this",
  survey_gap: "Not learned yet",
  contradiction: "Answers disagree",
  proposed_fact: "We heard this from you",
  upload_request: "A file answers this best",
  steward_request: "A steward asked",
  recertification: "Please re-confirm",
  admin: "Requested by your team",
};

export function TasksPage() {
  const { organizationId } = useCurrentOrganization();
  const { can } = useAuth();
  const navigate = useNavigate();
  const canAssign = can(organizationId, "tasks:assign");
  const [scope, setScope] = useState<"mine" | "everyone">(
    canAssign ? "everyone" : "mine",
  );
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState<string | undefined>();

  const list = useAsync(
    () =>
      organizationId
        ? tasksApi.list(organizationId, { scope })
        : Promise.resolve([]),
    [organizationId, scope],
  );
  const summary = useAsync(
    () =>
      organizationId
        ? tasksApi.summary(organizationId, scope)
        : Promise.resolve(undefined),
    [organizationId, scope, list.data],
  );
  const people = useAsync<PersonOption[]>(
    () =>
      organizationId && canAssign
        ? (organizationsApi.people(organizationId) as unknown as Promise<
            PersonOption[]
          >)
        : Promise.resolve([]),
    [organizationId, canAssign],
  );

  if (!organizationId) {
    return (
      <>
        <PageHeader title="My context tasks" />
        <Card>
          <EmptyState title="No organization selected" />
        </Card>
      </>
    );
  }

  async function run(id: string, action: () => Promise<unknown>) {
    setBusy(id);
    setError(undefined);
    try {
      await action();
      list.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(undefined);
    }
  }

  async function start(task: ContextTask) {
    setBusy(task.id);
    setError(undefined);
    try {
      const created = await tasksApi.start(organizationId!, task.id);
      navigate(`/interviews/${created.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(undefined);
    }
  }

  const rows = list.data ?? [];
  const s = summary.data;
  return (
    <>
      <PageHeader
        title={scope === "mine" ? "My context tasks" : "Context tasks"}
        description="What still needs a person's input so the company's context becomes known. Each item is one question, one confirmation, one conflict or one file."
        actions={
          canAssign ? (
            <div className="row" style={{ gap: 8 }}>
              <select
                aria-label="Scope"
                value={scope}
                onChange={(event) =>
                  setScope(event.target.value as "mine" | "everyone")
                }
              >
                <option value="mine">Mine</option>
                <option value="everyone">Everyone</option>
              </select>
              <button
                disabled={busy === "derive"}
                onClick={() =>
                  void run("derive", () => tasksApi.derive(organizationId!))
                }
              >
                Recompute tasks
              </button>
            </div>
          ) : undefined
        }
      />
      <div className="stack">
        <ErrorBanner error={error ?? list.error} />
        {s ? (
          <div className="grid grid--stats" data-testid="task-summary">
            <Stat
              label="Questions need your input"
              value={s.questions}
              tone={s.questions ? "warn" : "ok"}
            />
            <Stat
              label="Things to confirm"
              value={s.confirmations}
              tone={s.confirmations ? "warn" : "ok"}
            />
            <Stat
              label="Conflicts to clarify"
              value={s.conflicts}
              tone={s.conflicts ? "danger" : "ok"}
            />
            <Stat label="Files to upload" value={s.uploads} />
            {scope === "everyone" ? (
              <Stat
                label="Unassigned"
                value={s.unassigned}
                tone={s.unassigned ? "warn" : undefined}
              />
            ) : null}
          </div>
        ) : null}
        <Card
          title={rows.length ? `${rows.length} open` : "Nothing waiting"}
          flush
        >
          {list.loading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState
              title={
                scope === "mine"
                  ? "Nothing needs you right now"
                  : "No open tasks"
              }
              description="When Enterprise Context reports a gap, an answer needs confirming, or two answers disagree, it shows up here."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>What we need</th>
                  <th>Why</th>
                  {scope === "everyone" ? <th>Who</th> : null}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((task) => (
                  <tr key={task.id} data-testid="task-row">
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <span
                          className={`badge badge--${task.priority === "high" ? "required" : "neutral"}`}
                        >
                          {TYPE_LABEL[task.task_type]}
                        </span>
                        <strong>{task.title}</strong>
                      </div>
                      {task.concept ? (
                        <div className="tiny faint mono">{task.concept}</div>
                      ) : null}
                    </td>
                    <td className="small muted">
                      <div>{ORIGIN_LABEL[task.origin] ?? task.origin}</div>
                      {task.reason ? (
                        <div className="tiny faint">{task.reason}</div>
                      ) : null}
                      <div className="tiny faint">
                        {relativeTime(task.created_at)}
                      </div>
                    </td>
                    {scope === "everyone" ? (
                      <td className="small">
                        {task.assigned_person_id
                          ? (people.data?.find(
                              (p) => p.id === task.assigned_person_id,
                            )?.full_name ?? "assigned")
                          : task.assigned_department_id
                            ? "a department"
                            : task.role_hint
                              ? `needs: ${task.role_hint.replace(/_/g, " ")}`
                              : "unassigned"}
                        {canAssign && people.data?.length ? (
                          <select
                            aria-label="Assign to"
                            value=""
                            onChange={(event) => {
                              const personId = event.target.value;
                              if (personId)
                                void run(task.id, () =>
                                  tasksApi.assign(organizationId!, task.id, {
                                    person_id: personId,
                                  }),
                                );
                            }}
                          >
                            <option value="">Assign…</option>
                            {people.data.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.full_name}
                              </option>
                            ))}
                          </select>
                        ) : null}
                      </td>
                    ) : null}
                    <td>
                      <div className="row" style={{ gap: 5 }}>
                        {task.task_type === "ANSWER_QUESTION" ||
                        task.task_type === "UPLOAD_EVIDENCE" ||
                        task.task_type === "RECERTIFY_CONTEXT" ? (
                          task.session_id ? (
                            <Link
                              className="btn btn--small"
                              to={`/interviews/${task.session_id}`}
                            >
                              Continue
                            </Link>
                          ) : (
                            <button
                              className="btn--small primary"
                              disabled={busy === task.id}
                              onClick={() => void start(task)}
                            >
                              {task.task_type === "UPLOAD_EVIDENCE"
                                ? "Answer or upload"
                                : "Answer"}
                            </button>
                          )
                        ) : task.task_type === "CONFIRM_OBSERVATION" ? (
                          <Link className="btn btn--small" to="/knowledge">
                            Confirm
                          </Link>
                        ) : (
                          <Link className="btn btn--small" to="/gaps">
                            Clarify
                          </Link>
                        )}
                        {canAssign ? (
                          <button
                            className="btn--small"
                            disabled={busy === task.id}
                            onClick={() => {
                              const note = window.prompt(
                                "Why is this not worth asking?",
                              );
                              if (note)
                                void run(task.id, () =>
                                  tasksApi.waive(
                                    organizationId!,
                                    task.id,
                                    note,
                                  ),
                                );
                            }}
                          >
                            Waive
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
